import { usePopup } from '../context/PopupContext';
import { Shield, FileText, Scale, Building, Briefcase, Globe, Users, Award, Clock, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useCallback, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import RevisedPricingSection from '../components/RevisedPricingSection';
import TestimonialsSlider from '../components/TestimonialsSlider';
import CTABanner from '../components/CTABanner';
import FAQSection from '../components/FAQSection';
import LeadForm from '../components/LeadForm';
import homeImg from '../assets/images/home.png';

const serviceHighlights = [
  { icon: Shield, title: "Trademark Registration Services USA", desc: "We provide complete USA trademark registration and USPTO trademark filing services to protect your brand name, logo, and slogan with strong federal trademark protection across all states and industries.", link: "/services/register-your-trademark" },
  { icon: Building, title: "Company Formation Services", desc: "Our business formation services include LLC, corporation, and non-profit setup with proper documentation, state compliance, and end-to-end support for starting your business smoothly.", link: "/services/limited-liability-company" },
  { icon: FileText, title: "Copyright Protection Services", desc: "We offer trusted copyright registration services to protect your creative works, including books, digital content, software, designs, and other intellectual property assets.", link: "/services/register-your-copyright" },
  { icon: Scale, title: "Patent Services", desc: "Our patent services USA include patent search, drafting, and filing support to help inventors and businesses protect their innovations and new product ideas.", link: "/services/search-for-existing-patent" },
  { icon: Briefcase, title: "Legal Services for Businesses", desc: "We provide professional business legal services covering corporate law, contracts, immigration support, and compliance advisory for smooth business operations.", link: "/services/business-immigration-law" },
  { icon: Globe, title: "Corporate & Brand Services", desc: "Our corporate services USA include Amazon Brand Registry, bookkeeping, tax advisory, and brand growth solutions to support your business expansion.", link: "/services/amazon-brand-registry" }
];

const stats = [
  { num: "97%", label: "Approval Success " },
  { num: "98%", label: "Client Satisfaction " },
  { num: "50", label: "States Covered" },
  { num: "24/7", label: "Expert Support" }
];

const whyChoose = [
  { icon: Users, title: "Expert Legal Team", desc: "Our seasoned IP professionals bring decades of combined experience in USA trademark registration and brand protection services. We protect your brand with precision, legal expertise, and full compliance at every stage." },
  { icon: Award, title: "Proven Track Record", desc: "We have successfully handled thousands of USPTO trademark registrations with one of the highest approval rates in the industry. Our proven results help businesses secure reliable federal trademark protection with confidence." },
  { icon: Clock, title: "Fast & Efficient", desc: "Our streamlined trademark filing services ensure your application is prepared and submitted quickly without delays. We maintain speed and accuracy while ensuring full compliance with USPTO requirements." },
  { icon: CheckCircle, title: "End-to-End Service", desc: "From initial trademark search to final approval, we manage the complete USA trademark registration process. Our team handles everything to provide a smooth, guided, and stress-free experience." }
];

export default function HomePage() {
  const { openPopup } = usePopup();
  const [serviceIndex, setServiceIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [whyChooseIndex, setWhyChooseIndex] = useState(0);
  const [isAnimatingWhyChoose, setIsAnimatingWhyChoose] = useState(false);
  
  // Drag state for services slider
  const [serviceDrag, setServiceDrag] = useState({ isDragging: false, startX: 0, currentX: 0 });
  const [whyChooseDrag, setWhyChooseDrag] = useState({ isDragging: false, startX: 0, currentX: 0 });

  const totalServices = serviceHighlights.length;
  const maxServiceIndex = totalServices - 3;
  const totalWhyChoose = whyChoose.length;
  const maxWhyChooseIndex = totalWhyChoose - 2;

  const slideNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setServiceIndex((prev) => (prev >= maxServiceIndex ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, maxServiceIndex]);

  const slidePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setServiceIndex((prev) => (prev <= 0 ? maxServiceIndex : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, maxServiceIndex]);

  const goToSlide = useCallback((index) => {
    if (isAnimating || index > maxServiceIndex) return;
    setIsAnimating(true);
    setServiceIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, maxServiceIndex]);

  const whyChooseNext = useCallback(() => {
    if (isAnimatingWhyChoose) return;
    setIsAnimatingWhyChoose(true);
    setWhyChooseIndex((prev) => prev + 1);
    setTimeout(() => setIsAnimatingWhyChoose(false), 500);
  }, [isAnimatingWhyChoose]);

  const whyChoosePrev = useCallback(() => {
    if (isAnimatingWhyChoose) return;
    setIsAnimatingWhyChoose(true);
    setWhyChooseIndex((prev) => prev - 1);
    setTimeout(() => setIsAnimatingWhyChoose(false), 500);
  }, [isAnimatingWhyChoose]);

  const goToWhyChooseSlide = useCallback((index) => {
    if (isAnimatingWhyChoose || index > maxWhyChooseIndex) return;
    setIsAnimatingWhyChoose(true);
    setWhyChooseIndex(index);
    setTimeout(() => setIsAnimatingWhyChoose(false), 500);
  }, [isAnimatingWhyChoose, maxWhyChooseIndex]);

  // Drag handlers for services slider
  const handleServiceMouseDown = useCallback((e) => {
    setServiceDrag({ isDragging: true, startX: e.clientX, currentX: 0 });
  }, []);

  const handleServiceMouseMove = useCallback((e) => {
    if (!serviceDrag.isDragging) return;
    const diff = e.clientX - serviceDrag.startX;
    setServiceDrag(prev => ({ ...prev, currentX: diff }));
  }, [serviceDrag.isDragging, serviceDrag.startX]);

  const handleServiceMouseUp = useCallback(() => {
    if (!serviceDrag.isDragging) return;
    const threshold = 50;
    if (serviceDrag.currentX > threshold) {
      slidePrev();
    } else if (serviceDrag.currentX < -threshold) {
      slideNext();
    }
    setServiceDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [serviceDrag.isDragging, serviceDrag.currentX, slidePrev, slideNext]);

  const handleServiceMouseLeave = useCallback(() => {
    if (serviceDrag.isDragging) {
      setServiceDrag({ isDragging: false, startX: 0, currentX: 0 });
    }
  }, [serviceDrag.isDragging]);

  // Drag handlers for why choose slider
  const handleWhyChooseMouseDown = useCallback((e) => {
    setWhyChooseDrag({ isDragging: true, startX: e.clientX, currentX: 0 });
  }, []);

  const handleWhyChooseMouseMove = useCallback((e) => {
    if (!whyChooseDrag.isDragging) return;
    const diff = e.clientX - whyChooseDrag.startX;
    setWhyChooseDrag(prev => ({ ...prev, currentX: diff }));
  }, [whyChooseDrag.isDragging, whyChooseDrag.startX]);

  const handleWhyChooseMouseUp = useCallback(() => {
    if (!whyChooseDrag.isDragging) return;
    const threshold = 50;
    if (whyChooseDrag.currentX > threshold) {
      whyChoosePrev();
    } else if (whyChooseDrag.currentX < -threshold) {
      whyChooseNext();
    }
    setWhyChooseDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [whyChooseDrag.isDragging, whyChooseDrag.currentX, whyChoosePrev, whyChooseNext]);

  const handleWhyChooseMouseLeave = useCallback(() => {
    if (whyChooseDrag.isDragging) {
      setWhyChooseDrag({ isDragging: false, startX: 0, currentX: 0 });
    }
  }, [whyChooseDrag.isDragging]);

  // Auto-play and infinite loop for Why Choose Us slider
  useEffect(() => {
    const interval = setInterval(() => {
      whyChooseNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [whyChooseNext]);

  // Reset index when it goes out of bounds for infinite loop
  useEffect(() => {
    if (whyChooseIndex > totalWhyChoose) {
      setIsAnimatingWhyChoose(true);
      setWhyChooseIndex(0);
      setTimeout(() => setIsAnimatingWhyChoose(false), 100);
    } else if (whyChooseIndex < 0) {
      setIsAnimatingWhyChoose(true);
      setWhyChooseIndex(totalWhyChoose);
      setTimeout(() => setIsAnimatingWhyChoose(false), 100);
    }
  }, [whyChooseIndex, totalWhyChoose]);

  return (
    <main data-testid="home-page">
      <SEOHead
        title="Trademark Legal Forum | Trademark Registration & IP Protection Services"
        description="Protect your brand with federal trademark registration starting at $45. Expert trademark, copyright, patent, and business formation services across all 50 US states."
      />
      {/* 1. Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/90678b74-8893-4692-bfc7-d7847fcc33f8/images/c927193ed8f5a9f15489e086f7bfe3ce2b3009562c75ee2a007e373fdb65b63a.png"
            alt="Hero background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1128]/80" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-sm tracking-[0.2em] font-bold text-[#D4AF37] mb-6 font-['Outfit'] animate-fade-up">
                Trusted Trademark Services in the USA
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight leading-none mb-6 animate-fade-up stagger-1">
                Take the First Step<br />
                <span className="gold-gradient-text">Toward Brand Protection</span>
              </h1>
              <p className="text-base md:text-lg text-[#A0AABF] leading-relaxed mb-8 max-w-xl animate-fade-up stagger-2">
                In today's competitive marketplace, your brand is one of your most valuable assets, and USA trademark registration is essential for long-term security. A federally registered trademark with the USPTO trademark registration gives you exclusive rights to use your brand name, logo, or slogan across the entire United States.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-up stagger-3">
                <button
                  data-testid="hero-register-btn"
                  onClick={() => openPopup()}
                  className="bg-[#D4AF37] text-[#0A1128] font-bold py-4 px-10 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-1 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wider uppercase"
                >
                  Register My Trademark
                </button>
                <button
                  data-testid="hero-chat-btn"
                  className="js-chat border border-[#D4AF37]/50 text-[#D4AF37] font-bold py-4 px-10 rounded-sm text-sm transition-all duration-300 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] hover:-translate-y-1 font-['Outfit'] tracking-wider uppercase"
                >
                  Chat Now
                </button>
              </div>
            </div>

            {/* Banner Form */}
            <div data-testid="home-banner-form" className="bg-[#121E3D]/90 backdrop-blur-xl border border-[#1D2D50] rounded-sm p-6 md:p-8 shadow-2xl animate-fade-up stagger-2">
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Take the First Step Toward Brand Protection</h3>
              <p className="text-[#A0AABF] text-sm mb-6">Fill in your details and our experts will contact you.</p>
              <LeadForm isInline />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Info Section with Image */}
      <section data-testid="info-section" className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Why Trademark Registration Matters</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 font-['Outfit'] tracking-tight mb-6">
                Your Brand Deserves Federal Protection
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                In today’s competitive marketplace, your brand is one of your most valuable assets, and USA trademark registration is essential for long-term security. A federally registered trademark with the USPTO trademark registration gives you exclusive rights to use your brand name, logo, or slogan across the entire United States.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Without proper trademark protection services, your brand remains vulnerable to copycats, infringement, and costly legal disputes. Completing your trademark filing services with the USPTO establishes legal ownership, deters competitors, and gives you the strong foundation needed to enforce your rights and protect your business identity.
              </p>
              <button
                data-testid="info-register-btn"
                onClick={() => openPopup()}
                className="bg-[#D4AF37] text-[#0A1128] font-bold py-3 px-8 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wide"
              >
                Register My Trademark
              </button>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4AF37]/5 rounded-sm" />
              <img
                src={homeImg}
                alt="Legal expertise"
                className="relative rounded-sm w-full h-auto object-cover shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#D4AF37]/30 rounded-sm" />
            </div>
          </div>
        </div>
      </section>

      {/* 3. Services Overview */}
      <section data-testid="services-overview" className="py-20 md:py-32 bg-[#0A1128]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Our Trademark Registration Services</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight">
              Comprehensive Intellectual Property & Business Solutions
            </h2>
          </div>
          
          <div className="relative">
            <style>{`
              .service-slider {
                --translate-percent: -34%;
              }
              @media (max-width: 639px) {
                .service-slider {
                  --translate-percent: -108%;
                }
              }
              @media (min-width: 640px) and (max-width: 1023px) {
                .service-slider {
                  --translate-percent: -50%;
                }
              }
            `}</style>
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out gap-6 service-slider cursor-grab active:cursor-grabbing"
                style={{ 
                  '--slide-index': serviceIndex,
                  transform: `translateX(calc(var(--slide-index) * var(--translate-percent)))`
                }}
                onMouseDown={handleServiceMouseDown}
                onMouseMove={handleServiceMouseMove}
                onMouseUp={handleServiceMouseUp}
                onMouseLeave={handleServiceMouseLeave}
              >
                {serviceHighlights.map((svc, i) => {
                  const Icon = svc.icon;
                  return (
                    <Link
                      key={i}
                      to={svc.link}
                      data-testid={`service-card-${i}`}
                      className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-0.666rem)] flex-shrink-0 bg-[#000000] border border-[#1D2D50] rounded-sm p-8 hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300 group"
                    >
                      <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors">
                        <Icon size={22} className="text-[#D4AF37]" />
                      </div>
                      <h3 className="text-lg font-bold text-white font-['Outfit'] mb-3 group-hover:text-[#D4AF37] transition-colors">{svc.title}</h3>
                      <p className="text-[#A0AABF] text-sm leading-relaxed">{svc.desc}</p>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Nav arrows */}
            <button
              onClick={slidePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={slideNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
            >
              <ChevronRight size={18} />
            </button>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: maxServiceIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    serviceIndex === i ? 'bg-[#D4AF37] w-6' : 'bg-[#1D2D50]'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Pricing Section */}
      <RevisedPricingSection />

      {/* 5. Why Choose Us */}
      <section data-testid="why-choose-section" className="py-20 md:py-32 bg-[#0D1729]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Why We Stand Out</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight">
              Benefits of Trademark Protection in USA 
            </h2>
          </div>
          
          <div className="relative">
            <style>{`
              .why-choose-slider {
                --translate-percent: -25%;
              }
              @media (max-width: 639px) {
                .why-choose-slider {
                  --translate-percent: -108%;
                }
              }
              @media (min-width: 640px) and (max-width: 1023px) {
                .why-choose-slider {
                  --translate-percent: -50%;
                }
              }
            `}</style>
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out gap-6 why-choose-slider cursor-grab active:cursor-grabbing"
                style={{ 
                  '--slide-index': whyChooseIndex,
                  transform: `translateX(calc(var(--slide-index) * var(--translate-percent)))`
                }}
                onMouseDown={handleWhyChooseMouseDown}
                onMouseMove={handleWhyChooseMouseMove}
                onMouseUp={handleWhyChooseMouseUp}
                onMouseLeave={handleWhyChooseMouseLeave}
              >
                {/* Duplicate items for continuous scrolling */}
                {[...whyChoose, ...whyChoose].map((item, i) => {
                  const Icon = item.icon;
                  const originalIndex = i % whyChoose.length;
                  return (
                    <div key={`${originalIndex}-${i}`} data-testid={`why-choose-${originalIndex}`} className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-0.7rem)] flex-shrink-0 bg-[#000000] border border-[#1D2D50] rounded-sm p-8 hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300">
                      <Icon size={28} className="text-[#D4AF37] mb-4" />
                      <h3 className="text-lg font-bold text-white font-['Outfit'] mb-3">{item.title}</h3>
                      <p className="text-[#A0AABF] text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Nav arrows */}
            <button
              onClick={whyChoosePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-[#0D1729] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={whyChooseNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-[#0D1729] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
            >
              <ChevronRight size={18} />
            </button>

            {/* Continuous indicator */}
            <div className="flex justify-center mt-8">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      Math.floor(whyChooseIndex % 3) === i ? 'bg-[#D4AF37]' : 'bg-[#1D2D50]/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Testimonials */}
      <TestimonialsSlider />

      {/* 8. Stats Section */}
      <section data-testid="stats-section" className="py-20 md:py-28 bg-[#0A1128] border-y border-[#1D2D50]/30">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-center">
                <p className="text-3xl md:text-4xl font-bold gold-gradient-text font-['Outfit'] mb-2">{stat.num}</p>
                <p className="text-[#A0AABF] text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ Section */}
      <FAQSection />

      {/* 10. CTA Banner */}
      <CTABanner />
    </main>
  );
}
